import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatInstantWithZone } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { AppointmentEmail, AppointmentEmails } from '@/routes/appointments/useAppointmentEmails';

/** Outcomes that are a problem the office should look at — styled as such (M2-16's set). */
const PROBLEM = new Set(['Bounced', 'Dropped', 'Failed']);

/**
 * Appointment emails and what became of each (M2-21, Kyle's 11): under each
 * visit on the job page, and the booking's own (the confirmation, a whole-job
 * cancellation, anything about a visit since removed) under the visits. The
 * office's only — the API sends anyone else `appointmentEmails: null` (M2-16
 * rule 5c), and the caller renders nothing for null.
 *
 * Each row is the kind, who it went to, ONE instant — delivery, else sent,
 * else queued, M2-16's rule — and the outcome as a label, an unknown word
 * shown as the API spelt it. `NotSent`'s reason is a code, shown as a
 * sentence ("opted-out" among them); a bounce's reason is the provider's
 * vocabulary, as it comes.
 *
 * The time is on the clock the caller passes, zone named: the job's
 * `appointmentTimezone` (the property's, else the account's), the clock the
 * API's sentences and the emails themselves are on.
 */
export function AppointmentEmailList({
  emails,
  recipients,
  timeZone,
  testId,
}: {
  emails: readonly AppointmentEmail[];
  recipients: AppointmentEmails;
  timeZone: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  if (emails.length === 0) return null;

  return (
    <ul className="space-y-1" data-testid={testId}>
      {emails.map((email) => {
        const when = email.deliveryAt ?? email.sentAt ?? email.queuedAt;
        const reason = reasonText(t, email);
        return (
          <li
            key={email.id}
            className="flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5 text-xs"
            data-testid="appointment-email"
            data-outcome={email.outcome}
          >
            <div className="min-w-0">
              <p data-testid="appointment-email-what">
                <span className="font-medium">{kindLabel(t, email.kind)}</span>
                {' · '}
                {recipientLabel(t, email.recipient, recipients)}
              </p>
              <p className="text-[var(--color-muted-foreground)]" data-testid="appointment-email-when">
                {formatInstantWithZone(when, timeZone)}
              </p>
              {reason ? (
                <p className="text-[var(--color-muted-foreground)]" data-testid="appointment-email-reason">
                  {reason}
                </p>
              ) : null}
            </div>
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 font-medium',
                PROBLEM.has(email.outcome)
                  ? 'border-[var(--color-destructive)] text-[var(--color-destructive)]'
                  : 'border-[var(--color-border)]',
              )}
              data-testid="appointment-email-outcome"
            >
              {t(`appointmentEmails.outcome.${email.outcome}`, { defaultValue: email.outcome })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function kindLabel(t: TFunction, kind: string): string {
  return t(`appointmentEmails.kind.${kind}`, { defaultValue: kind });
}

/** "To Pat Morrow (customer)": the recipient's name as the job reads it now. */
function recipientLabel(t: TFunction, recipient: string, recipients: AppointmentEmails): string {
  const person = recipient === 'Occupant' ? recipients.occupant : recipient === 'Customer' ? recipients.customer : null;
  const role = t(`appointmentEmails.recipient.${recipient}`, { defaultValue: recipient });
  return person?.name
    ? t('appointmentEmails.toNamed', { name: person.name, role })
    : t('appointmentEmails.toRole', { role });
}

function reasonText(t: TFunction, email: AppointmentEmail): string | null {
  if (!email.reason) return null;
  if (email.outcome === 'NotSent') {
    return t(`appointmentEmails.notSent.${email.reason}`, { defaultValue: email.reason });
  }
  return email.reason;
}

/**
 * Who the job's appointment emails go to, as the office sees it (Kyle's 3):
 * the customer and the property's primary occupant, each with the address it
 * would go to now and whether they get appointment emails at all.
 */
export function AppointmentRecipients({ recipients }: { recipients: AppointmentEmails }) {
  const { t } = useTranslation();
  const rows = [
    { key: 'Customer', person: recipients.customer },
    ...(recipients.occupant ? [{ key: 'Occupant', person: recipients.occupant }] : []),
  ];
  return (
    <ul className="space-y-0.5 text-xs" data-testid="appointment-recipients">
      {rows.map(({ key, person }) => (
        <li key={key} data-testid={`appointment-recipient-${key}`}>
          <span className="font-medium">
            {person.name ?? t(`appointmentEmails.recipientUnnamed.${key}`)}
          </span>{' '}
          <span className="text-[var(--color-muted-foreground)]">
            ({t(`appointmentEmails.recipient.${key}`)})
          </span>
          {': '}
          {!person.enabled
            ? t('appointmentEmails.recipientOff')
            : key === 'Occupant' && person.sameAddressAsCustomer
              ? t('appointmentEmails.recipientSameAddress')
              : person.address ?? t('appointmentEmails.recipientNoAddress')}
        </li>
      ))}
    </ul>
  );
}
